import { ArrowLeft } from 'lucide-react';

interface PrivacyPolicyProps {
  onBack?: () => void;
}

export default function PrivacyPolicy({ onBack }: PrivacyPolicyProps) {
  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      window.history.back();
    }
  };

  return (
    <div className="flex min-h-screen w-full flex-col bg-[#f8f5f5] overflow-x-hidden">
      {/* Header */}
      <div className="flex items-center p-4 pb-2 justify-between sticky top-0 bg-[#f8f5f5]/80 backdrop-blur-md z-10">
        <button
          onClick={handleBack}
          className="size-10 rounded-full hover:bg-slate-200/50 flex items-center justify-center transition-colors"
        >
          <ArrowLeft size={24} className="text-slate-600" />
        </button>
        <h1 className="text-lg font-bold text-slate-900">Privacy Policy</h1>
        <div className="size-10" />
      </div>

      <div className="px-5 pb-8 mt-2">
        <div className="bg-white rounded-2xl shadow-sm p-5 space-y-5">
          <p className="text-xs text-slate-400">Last updated: March 25, 2026</p>

          <Section title="The Short Version">
            <p>
              Nudge helps you stay in touch with the people you care about. We collect only what we
              need to make the app work, we encrypt your personal data, and we never sell or share
              it with anyone. You can export or delete your data at any time.
            </p>
          </Section>

          <Section title="1. What We Collect">
            <p>When you create an account, we collect:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>
                <strong>Account info</strong> — your name, email address, and password (hashed, we
                never store it in plain text)
              </li>
              <li>
                <strong>Contacts you add</strong> — names, phone numbers, emails, birthdays, and
                notes you choose to enter
              </li>
              <li>
                <strong>Interactions you log</strong> — type, date, and optional notes
              </li>
              <li>
                <strong>Device info</strong> — push notification token and timezone (for sending
                reminders at the right time)
              </li>
            </ul>
            <p className="mt-2">
              We do <strong>not</strong> access your phone&apos;s contact list, call logs, messages,
              or location unless you explicitly import contacts.
            </p>
          </Section>

          <Section title="2. How We Use Your Data">
            <ul className="list-disc pl-5 space-y-1">
              <li>Calculate relationship health scores and show your dashboard</li>
              <li>Send you reminders about birthdays and overdue contacts</li>
              <li>Sync your data across devices</li>
              <li>Respond to support requests</li>
            </ul>
            <p className="mt-2">
              That&apos;s it. We don&apos;t use your data for advertising, analytics, profiling, or
              anything else.
            </p>
          </Section>

          <Section title="3. How We Protect Your Data">
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <strong>Encryption at rest</strong> — your contact names, phone numbers, notes, and
                interaction details are encrypted with AES-256-GCM before they reach our database.
                Even with direct database access, your data appears as unreadable ciphertext.
              </li>
              <li>
                <strong>Encryption in transit</strong> — all communication between your device and
                our servers uses HTTPS/TLS.
              </li>
              <li>
                <strong>Password security</strong> — passwords are hashed with bcrypt. We never
                store or see your password.
              </li>
              <li>
                <strong>No admin access to your data</strong> — there is no admin interface that
                displays your personal contacts, notes, or conversations.
              </li>
            </ul>
          </Section>

          <Section title="4. Who We Share Data With">
            <p>
              <strong>Nobody.</strong> We do not sell, rent, trade, or share your personal data with
              any third party. The only external services we use are:
            </p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>
                <strong>Firebase Cloud Messaging</strong> — to deliver push notifications to your
                device. We send only notification metadata, not your personal data.
              </li>
              <li>
                <strong>DigitalOcean</strong> — to host our servers and database (encrypted at
                rest).
              </li>
            </ul>
          </Section>

          <Section title="5. Your Rights">
            <p>You have full control over your data:</p>
            <ul className="list-disc pl-5 space-y-1 mt-2">
              <li>
                <strong>Export</strong> — download all your data as JSON anytime from Privacy &
                Security settings
              </li>
              <li>
                <strong>Delete</strong> — request account deletion from the app. Your data is
                permanently removed within 30 days.
              </li>
              <li>
                <strong>Correct</strong> — edit or remove any contact or interaction at any time
              </li>
            </ul>
          </Section>

          <Section title="6. Data Retention">
            <p>
              We keep your data as long as your account is active. If you delete your account, all
              your data is permanently erased within 30 days. We do not keep backups of deleted
              accounts.
            </p>
          </Section>

          <Section title="7. Children">
            <p>
              Nudge is not intended for children under 13. We do not knowingly collect data from
              children. If you believe a child has created an account, please contact us and we will
              delete it.
            </p>
          </Section>

          <Section title="8. Changes to This Policy">
            <p>
              If we make significant changes to this policy, we&apos;ll notify you through the app
              before the changes take effect. Continued use after notification means you accept the
              updated policy.
            </p>
          </Section>

          <Section title="9. Contact Us">
            <p>
              Questions or concerns? Reach out through the Support & Feedback section in the app, or
              email us at <strong>privacy@trynudge.xyz</strong>.
            </p>
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h2 className="text-sm font-bold text-slate-800 mb-2">{title}</h2>
      <div className="text-[13px] text-slate-600 leading-relaxed">{children}</div>
    </div>
  );
}
